import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { line as d3line, area as d3area, curveCatmullRom } from 'd3-shape'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import { fetchStrategyAllocation } from '../../api/strategy'
import { fetchRebalanceCheck } from '../../api/rebalance'
import { fetchNavHistory, type NavPeriod, type NavPoint } from '../../api/portfolio'
import type { MarketContext, StageAllocation, StrategyAllocationResponse, StrategyMode } from '../../types/strategy'
import type { RebalanceAction, RebalanceCheckResponse } from '../../types/rebalance'
import { fetchWalletBalances } from '../../api/wallet'
import type { WalletBalance } from '../../types/wallet'
import type { LendingPosition } from '../../types/lending'
import JupiterBorrowPanel from './JupiterBorrowPanel'
import LpManagePanel from './LpManagePanel'
import JupiterPerpsPanel from './JupiterPerpsPanel'
import SuggestionCard from '../shared/SuggestionCard'
import JupiterSwapPanel from './JupiterSwapPanel'
import StrategyGuidePanel from './StrategyGuidePanel'
import type { WalletTopUpSuggestion } from '../../types/strategy'

const SOL_MINT    = 'So11111111111111111111111111111111111111112'
const JUPSOL_MINT = 'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v'
const USDC_MINT   = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'

// Per-stage: asset symbol + mint address for price lookup
const STAGE_ASSET: Record<string, { symbol: string; mint: string }> = {
  LEND:   { symbol: 'jupSOL', mint: JUPSOL_MINT },
  BORROW: { symbol: 'USDC',   mint: USDC_MINT   },
  HEDGE:  { symbol: 'SOL',    mint: SOL_MINT    },
}

const MODES: { id: StrategyMode; label: string }[] = [
  { id: 'CONSERVATIVE_CARRY', label: 'Conservative' },
  { id: 'BALANCED_CARRY',     label: 'Balanced' },
  { id: 'MAX_EARN_CARRY',     label: 'Max Earn' },
]

// Map allocation stage names → rebalance action stage names
const STAGE_MAP: Record<string, string[]> = {
  LEND:   [],
  BORROW: ['BORROW'],
  LP:     ['LP'],
  HEDGE:  ['HEDGE'],
}

function fmt(n: number, decimals = 2) {
  return n.toLocaleString('en-US', { maximumFractionDigits: decimals, minimumFractionDigits: decimals })
}
function usd(n: number) { return '$' + fmt(n) }
function pct(n: number) { return (n * 100).toFixed(1) + '%' }

function statusColor(status: StageAllocation['status']) {
  switch (status) {
    case 'ACTIVE':      return 'bg-accent-green/20 text-accent-green'
    case 'RECOMMENDED': return 'bg-accent-purple/20 text-accent-purple'
    case 'SKIPPED':     return 'bg-surface-hover text-ink-muted'
    case 'BLOCKED':     return 'bg-red-500/20 text-red-400'
  }
}

function urgencyColor(u: RebalanceAction['urgency']) {
  return u === 'HIGH' ? 'text-red-400' : u === 'MEDIUM' ? 'text-amber-400' : 'text-ink-muted'
}
function urgencyBg(u: RebalanceAction['urgency']) {
  return u === 'HIGH' ? 'border-red-500/30 bg-red-500/5' : u === 'MEDIUM' ? 'border-yellow-400/30 bg-yellow-400/5' : 'border-fringe-strong bg-surface-hover'
}
function urgencyDot(u: RebalanceAction['urgency']) {
  const c = u === 'HIGH' ? 'bg-red-400' : u === 'MEDIUM' ? 'bg-yellow-400' : 'bg-gray-500'
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${c} shrink-0`} />
}

function overallStatusStyle(s: RebalanceCheckResponse['overallStatus']) {
  switch (s) {
    case 'IN_SYNC':          return 'bg-accent-green/20 text-accent-green'
    case 'MINOR_DRIFT':      return 'bg-yellow-400/20 text-yellow-300'
    case 'REBALANCE_NEEDED': return 'bg-orange-400/20 text-orange-300'
    case 'URGENT':           return 'bg-red-500/20 text-red-400'
  }
}
function overallStatusLabel(s: RebalanceCheckResponse['overallStatus']) {
  switch (s) {
    case 'IN_SYNC':          return 'In Sync'
    case 'MINOR_DRIFT':      return 'Minor Drift'
    case 'REBALANCE_NEEDED': return 'Rebalance'
    case 'URGENT':           return 'Urgent'
  }
}

const STAGE_FLOW: Record<string, string> = {
  LEND:   'deposit jupSOL',
  BORROW: 'draw USDC',
  LP:     'SOL + USDC → pool',
  HEDGE:  'short SOL perp',
}

function riskDot(level: StageAllocation['riskLevel']) {
  const c = level === 'LOW' ? 'bg-accent-green' : level === 'MEDIUM' ? 'bg-yellow-400' : 'bg-red-400'
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${c} shrink-0`} />
}
function riskScoreColor(score: number) {
  if (score < 0.35) return 'text-accent-green'
  if (score < 0.65) return 'text-amber-400'
  return 'text-red-400'
}

// Compact inline action card shown when a stage row is expanded
function ActionDetail({ action }: { action: RebalanceAction }) {
  const hasDelta = action.deltaUsd != null
  const pos = hasDelta && (action.deltaUsd ?? 0) > 0

  return (
    <div className={`rounded-lg border px-3 py-2 flex flex-col gap-1.5 ${urgencyBg(action.urgency)}`}>
      {/* Top row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-surface-hover text-ink-soft uppercase tracking-wide">
          {action.actionType}
        </span>
        <div className="flex items-center gap-1">
          {urgencyDot(action.urgency)}
          <span className={`text-[9px] font-medium uppercase ${urgencyColor(action.urgency)}`}>
            {action.urgency}
          </span>
        </div>
        {hasDelta && (
          <span className={`ml-auto text-[11px] font-semibold ${pos ? 'text-accent-green' : 'text-red-400'}`}>
            {pos ? '+' : ''}{usd(action.deltaUsd ?? 0)}
          </span>
        )}
        {action.deltaBase != null && (
          <span className={`text-[11px] font-semibold ${pos ? 'text-accent-green' : 'text-red-400'}`}>
            {pos ? '+' : ''}{fmt(Math.abs(action.deltaBase ?? 0), 4)} SOL
          </span>
        )}
      </div>

      {/* Description */}
      <p className="text-[11px] text-ink leading-snug">{action.description}</p>

      {/* Current → Target */}
      {(action.currentUsd > 0 || action.targetUsd > 0) && (
        <div className="flex items-center gap-1.5 text-[10px] text-ink-muted">
          <span>{usd(action.currentUsd)}</span>
          <span className="text-gray-400">→</span>
          <span className="text-ink">{usd(action.targetUsd)}</span>
        </div>
      )}

      {/* Hedge extras */}
      {(action.stopLossPrice != null || action.leverageSuggested != null) && (
        <div className="flex gap-3 text-[10px] text-ink-muted">
          {action.leverageSuggested != null && (
            <span>Leverage <span className="text-ink">{fmt(action.leverageSuggested, 2)}×</span></span>
          )}
          {action.stopLossPrice != null && (
            <span>SL <span className="text-ink">{usd(action.stopLossPrice)}</span><span className="text-gray-400"> / SOL</span></span>
          )}
        </div>
      )}

      {/* LP range */}
      {action.lpRangeLow != null && action.lpRangeHigh != null && (
        <div className="text-[10px] text-ink-muted">
          Range <span className="text-ink">{usd(action.lpRangeLow)}</span>
          <span className="text-gray-400"> – </span>
          <span className="text-ink">{usd(action.lpRangeHigh)}</span>
        </div>
      )}

      {/* Projected HF */}
      {action.projectedHfAfterAction != null && (
        <div className="text-[10px] text-ink-muted">
          HF after <span className="text-ink">{fmt(action.projectedHfAfterAction)}</span>
        </div>
      )}

      {/* Reason chips */}
      {action.reasonCodes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {action.reasonCodes.map(c => (
            <span key={c} className="text-[8px] px-1 py-0.5 rounded bg-surface-hover text-gray-500">{c}</span>
          ))}
        </div>
      )}
    </div>
  )
}

function RecommendedDetail({ stage, wsLp }: { stage: StageAllocation; wsLp?: import('../../types/lp').LpState[] | null }) {
  if (stage.stage === 'HEDGE') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Notional</span>
          <span className="text-[11px] font-semibold text-ink">{usd(stage.amountUsd)}</span>
          {stage.amountBase != null && (
            <span className="text-[9px] text-gray-500">{fmt(stage.amountBase, 3)} SOL</span>
          )}
        </div>
        <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Leverage</span>
          <span className="text-[11px] font-semibold text-ink">
            {stage.leverageFactor != null ? fmt(stage.leverageFactor, 2) + '×' : '—'}
          </span>
          {stage.availableMarginUsd != null && (
            <span className="text-[9px] text-gray-500">margin {usd(stage.availableMarginUsd)}</span>
          )}
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Stop Loss</span>
          <span className="text-[11px] font-semibold text-red-400">
            {stage.stopLossPrice != null
              ? <>{usd(stage.stopLossPrice)}<span className="text-[9px] font-normal text-red-400/70"> / SOL</span></>
              : '—'}
          </span>
          <span className="text-[9px] text-gray-500">close short above this price</span>
        </div>
      </div>
    )
  }

  if (stage.stage === 'LP') {
    const solUsd  = stage.lpSolRatio    != null ? stage.amountUsd * stage.lpSolRatio    : null
    const stableUsd = stage.lpStableRatio != null ? stage.amountUsd * stage.lpStableRatio : null
    const positions = wsLp ?? []
    const showTarget = stage.status === 'RECOMMENDED' || positions.length === 0
    return (
      <div className="flex flex-col gap-2">
        {/* Recommended target — show when nothing deployed yet, or when there is also a gap */}
        {showTarget && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-wide">Recommended target</span>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
                <span className="text-[9px] text-gray-500 uppercase tracking-wide">Range low (S)</span>
                <span className="text-[11px] font-semibold text-accent-green">
                  {stage.lpRangeLow != null ? usd(stage.lpRangeLow) : '—'}
                </span>
                <span className="text-[9px] text-gray-500">support level</span>
              </div>
              <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
                <span className="text-[9px] text-gray-500 uppercase tracking-wide">Range high (R)</span>
                <span className="text-[11px] font-semibold text-red-400">
                  {stage.lpRangeHigh != null ? usd(stage.lpRangeHigh) : '—'}
                </span>
                <span className="text-[9px] text-gray-500">resistance level</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface-hover rounded-lg p-2 flex flex-col gap-0.5">
                <span className="text-[9px] text-gray-500 uppercase tracking-wide">Swap → SOL</span>
                <span className="text-[11px] font-semibold text-ink">
                  {solUsd != null ? usd(solUsd) : '—'}
                </span>
                {stage.lpSolRatio != null && (
                  <span className="text-[9px] text-gray-500">{(stage.lpSolRatio * 100).toFixed(1)}% of capital</span>
                )}
              </div>
              <div className="bg-surface-hover rounded-lg p-2 flex flex-col gap-0.5">
                <span className="text-[9px] text-gray-500 uppercase tracking-wide">Keep USDC</span>
                <span className="text-[11px] font-semibold text-ink">
                  {stableUsd != null ? usd(stableUsd) : '—'}
                </span>
                {stage.lpStableRatio != null && (
                  <span className="text-[9px] text-gray-500">{(stage.lpStableRatio * 100).toFixed(1)}% of capital</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Current deployed positions — one card per LP position from the streaming feed */}
        {positions.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-wide">
              Current position{positions.length > 1 ? 's' : ''} ({positions.length})
            </span>
            {positions.map(p => {
              const inRange = p.inRange
              const value = parseFloat(p.lpValueUsd || '0')
              const fees = parseFloat(p.totalPendingYieldUsd || '0')
              const apr = parseFloat(p.totalAprPct || '0') * 100
              const il = parseFloat(p.ilPct || '0') * 100
              const occupancy = parseFloat(p.rangeOccupancy || '0') * 100
              return (
                <div key={p.positionMint || p.poolId} className="border border-fringe bg-surface-hover rounded-lg p-2 flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-ink">{p.pair}</span>
                      <span className="text-[9px] text-gray-500 uppercase">{p.provider}</span>
                    </div>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${inRange ? 'bg-accent-green/15 text-accent-green' : 'bg-red-500/15 text-red-400'}`}>
                      {inRange ? 'IN RANGE' : 'OUT OF RANGE'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    <div className="bg-surface-hover rounded p-1.5 flex flex-col gap-0">
                      <span className="text-[8px] text-gray-500 uppercase tracking-wide">Value</span>
                      <span className="text-[11px] font-semibold text-ink">{usd(value)}</span>
                      <span className="text-[8px] text-gray-500">
                        {parseFloat(p.token0Amount).toFixed(3)} + {parseFloat(p.token1Amount).toFixed(2)}
                      </span>
                    </div>
                    <div className="bg-surface-hover rounded p-1.5 flex flex-col gap-0">
                      <span className="text-[8px] text-gray-500 uppercase tracking-wide">Range</span>
                      <span className="text-[11px] font-semibold text-ink">
                        {usd(parseFloat(p.priceLower))}–{usd(parseFloat(p.priceUpper))}
                      </span>
                      <span className="text-[8px] text-gray-500">price {usd(parseFloat(p.currentPrice))}</span>
                    </div>
                    <div className="bg-surface-hover rounded p-1.5 flex flex-col gap-0">
                      <span className="text-[8px] text-gray-500 uppercase tracking-wide">APR</span>
                      <span className={`text-[11px] font-semibold ${apr >= 0 ? 'text-accent-green' : 'text-red-400'}`}>
                        {apr.toFixed(2)}%
                      </span>
                      <span className="text-[8px] text-gray-500">fees {usd(fees)}</span>
                    </div>
                    <div className="bg-surface-hover rounded p-1.5 flex flex-col gap-0">
                      <span className="text-[8px] text-gray-500 uppercase tracking-wide">IL / Occ.</span>
                      <span className={`text-[11px] font-semibold ${il < 0 ? 'text-red-400' : 'text-ink'}`}>
                        {il.toFixed(2)}%
                      </span>
                      <span className="text-[8px] text-gray-500">{occupancy.toFixed(0)}% in range</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  if (stage.stage === 'LOOP') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Max loops</span>
          <span className="text-[11px] font-semibold text-ink">{stage.loopDepth ?? '—'}</span>
          <span className={`text-[9px] ${stage.loopStatus === 'HOLD_NO_LOOP' ? 'text-red-400' : 'text-accent-green'}`}>
            {stage.loopStatus ?? '—'}
          </span>
        </div>
        <div className="bg-surface-hover rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Net spread</span>
          <span className={`text-[11px] font-semibold ${stage.yieldOrCostApr >= 0 ? 'text-accent-green' : 'text-red-400'}`}>
            {(stage.yieldOrCostApr >= 0 ? '+' : '') + pct(stage.yieldOrCostApr)}
          </span>
        </div>
        <div className="bg-surface-hover rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">HF after</span>
          <span className="text-[11px] font-semibold text-ink">
            {stage.projectedHf != null ? fmt(stage.projectedHf) : '—'}
          </span>
        </div>
      </div>
    )
  }

  if (stage.stage === 'BORROW') {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Borrow amount</span>
          <span className="text-[11px] font-semibold text-ink">{usd(stage.amountUsd)}</span>
          <span className="text-[9px] text-gray-500">against collateral</span>
        </div>
        <div className="bg-surface-hover rounded-lg p-2 flex flex-col gap-0.5">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">Borrow cost</span>
          <span className="text-[11px] font-semibold text-red-400">
            {pct(Math.abs(stage.yieldOrCostApr))} APR
          </span>
          <span className="text-[9px] text-gray-500">{usd(Math.abs(stage.dailyYieldOrCostUsd))}/day</span>
        </div>
      </div>
    )
  }

  // STAKE / LEND
  return (
    <div className="bg-accent-purple/10 border border-accent-purple/20 rounded-lg p-2 flex items-center gap-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] text-gray-500 uppercase tracking-wide">Deploy</span>
        <span className="text-[11px] font-semibold text-ink">{usd(stage.amountUsd)}</span>
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-[9px] text-gray-500 uppercase tracking-wide">Expected yield</span>
        <span className="text-[11px] font-semibold text-accent-green">
          +{usd(stage.dailyYieldOrCostUsd)}/day
        </span>
      </div>
    </div>
  )
}


const GAP_THRESHOLD = 0.02  // ±2%

function gapExceedsThreshold(stage: StageAllocation) {
  if (stage.gapUsd == null || stage.amountUsd <= 0) return false
  return Math.abs(stage.gapUsd) / stage.amountUsd > GAP_THRESHOLD
}

/**
 * Combines the highest urgency from any pending rebalance action with the
 * stage's gap-vs-target ratio so STAKE / LEND (which never get rebalance
 * actions because STAGE_MAP doesn't route to them) still light up when their
 * actual capital drifts far from the recommended target.
 */
type Urgency = 'HIGH' | 'MEDIUM' | 'LOW'
function effectiveUrgency(
  stage: StageAllocation,
  topUrgency: Urgency | null,
): Urgency | null {
  if (stage.status === 'SKIPPED') return null
  if (topUrgency) return topUrgency
  if (stage.gapUsd != null && stage.amountUsd > 0) {
    const ratio = Math.abs(stage.gapUsd) / stage.amountUsd
    if (ratio > 0.20) return 'HIGH'
    if (ratio > 0.10) return 'MEDIUM'
    if (ratio > GAP_THRESHOLD) return 'LOW'
  }
  return null
}

/** Background classes for the row's <td>s, keyed by computed urgency. */
function urgencyRowBg(level: Urgency | null, isClickable: boolean): string {
  switch (level) {
    case 'HIGH':   return `bg-red-500/15    ${isClickable ? 'group-hover:bg-red-500/25'    : ''}`
    case 'MEDIUM': return `bg-amber-500/15  ${isClickable ? 'group-hover:bg-amber-500/25'  : ''}`
    case 'LOW':    return `bg-yellow-400/10 ${isClickable ? 'group-hover:bg-yellow-400/20' : ''}`
    default:       return `bg-surface-hover ${isClickable ? 'group-hover:bg-surface-hover' : ''}`
  }
}

function gapWhyText(stage: StageAllocation): { action: string; why: string } {
  const isUnder = (stage.gapUsd ?? 0) > 0
  switch (stage.stage) {
    case 'LEND': return isUnder ? {
      action: 'Deposit more collateral',
      why: 'Your lending position is below target. More deposited collateral increases borrowing capacity and earns additional yield APR. Health factor improves as collateral grows relative to borrow.',
    } : {
      action: 'Withdraw excess collateral',
      why: 'You have deposited more than this strategy requires. Withdrawing the surplus frees capital for the reserve without materially reducing borrowing capacity or health factor.',
    }
    case 'BORROW': return isUnder ? {
      action: 'Draw additional USDC',
      why: 'Increasing the borrow deploys more capital into LP and Loop stages, amplifying overall yield. Health factor remains above the safe threshold after this adjustment.',
    } : {
      action: 'Repay excess USDC',
      why: 'You have borrowed more than recommended. Repaying the excess reduces daily interest cost and lifts health factor, creating more buffer against liquidation.',
    }
    case 'LP': return isUnder ? {
      action: 'Add liquidity to pool',
      why: 'Your LP position is under the optimal size for the current market regime. Adding capital increases your fee-income share proportionally without changing the price range exposure.',
    } : {
      action: 'Remove liquidity from pool',
      why: 'Your LP position exceeds the recommended size. Reducing it limits impermanent loss exposure and frees capital that can be redirected to higher-priority stages.',
    }
    case 'HEDGE': return isUnder ? {
      action: 'Increase short notional',
      why: 'Your current hedge is smaller than the recommended notional, leaving a portion of your SOL exposure unprotected. Increasing the short fully neutralises downside delta.',
    } : {
      action: 'Reduce short notional',
      why: 'You are over-hedged — the excess short position accumulates unnecessary funding costs and drags net carry. Reduce notional to the recommended size to optimise net yield.',
    }
    case 'LOOP': return isUnder ? {
      action: 'Execute additional loop',
      why: 'The loop strategy is running below its optimal depth. An additional iteration increases effective leverage and APY while keeping projected health factor within safe bounds.',
    } : {
      action: 'Unwind one loop iteration',
      why: 'Current loop depth exceeds the recommended level given health factor projections. Deleveraging one loop reduces liquidation risk while preserving most of the yield amplification.',
    }
    default: return isUnder ? {
      action: 'Increase allocation',
      why: 'This stage is under its recommended size. Increasing allocation brings the portfolio closer to optimal capital efficiency.',
    } : {
      action: 'Reduce allocation',
      why: 'This stage exceeds its recommended size. Reducing it aligns the portfolio with the current risk budget.',
    }
  }
}

function GapRecommendationPanel({
  stage, priceMap, basePrice, onClose,
}: {
  stage: StageAllocation
  priceMap: Record<string, number>
  basePrice: number
  mode: StrategyMode
  onClose: () => void
}) {
  const gap     = stage.gapUsd ?? 0
  const isUnder = gap > 0
  const gapPct  = stage.amountUsd > 0 ? Math.abs(gap) / stage.amountUsd : 0
  const { action, why } = gapWhyText(stage)
  const asset   = STAGE_ASSET[stage.stage]
  const price   = asset ? (priceMap[asset.mint] ?? basePrice) : basePrice
  const absGap  = Math.abs(gap)
  const nativeTarget = asset && price > 0 && stage.amountUsd > 0 ? stage.amountUsd / price : null
  const nativeActual = asset && price > 0 && stage.actualAmountUsd != null ? stage.actualAmountUsd / price : null

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-xs bg-surface-card border-l border-fringe-strong shadow-2xl flex flex-col h-full overflow-y-auto animate-slide-in-right"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-fringe-strong shrink-0">
          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${isUnder ? 'bg-yellow-400' : 'bg-orange-400'}`} />
          <span className="text-sm font-semibold text-ink flex-1">{stage.stage} — Rebalance needed</span>
          <button onClick={onClose} className="text-gray-500 hover:text-ink transition-colors p-1">
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/>
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-3 px-4 py-4 flex-1">
          {/* Current → Recommended summary */}
          <div className="flex items-center justify-between text-[11px] px-1">
            <div>
              <div className="text-[9px] text-gray-500">Current</div>
              <span className="text-ink font-medium">
                {stage.actualAmountUsd != null ? usd(stage.actualAmountUsd) : '—'}
              </span>
              {nativeActual != null && asset && (
                <span className="text-gray-500 ml-1 text-[10px]">{fmt(nativeActual, 4)} {asset.symbol}</span>
              )}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-gray-400 shrink-0">
              <path d="M5 12h14m-6-6 6 6-6 6"/>
            </svg>
            <div className="text-right">
              <div className="text-[9px] text-gray-500">Recommended</div>
              <span className="text-accent-green font-semibold">{usd(stage.amountUsd)}</span>
              {nativeTarget != null && asset && (
                <span className="text-gray-500 ml-1 text-[10px]">{fmt(nativeTarget, 4)} {asset.symbol}</span>
              )}
            </div>
          </div>

          {/* Suggestion card */}
          <SuggestionCard
            action={action}
            amount={`${isUnder ? '+' : '−'}${usd(absGap)}`}
            pctChange={`(${(gapPct * 100).toFixed(1)}%)`}
            isPositive={isUnder}
            description={why}
            parameters={stage.stage === 'HEDGE' ? [
              { label: 'Leverage', value: stage.leverageFactor != null ? fmt(stage.leverageFactor, 2) + '×' : '—', sublabel: 'short notional' },
              { label: 'Margin', value: stage.availableMarginUsd != null ? usd(stage.availableMarginUsd) : '—', sublabel: 'available' },
              { label: 'Stop loss', value: stage.stopLossPrice != null ? usd(stage.stopLossPrice) : '—', sublabel: 'close above', color: 'text-red-400' },
            ] : undefined}
            impacts={[
              { label: 'Stage APY', value: (stage.yieldOrCostApr >= 0 ? '+' : '') + pct(stage.yieldOrCostApr),
                color: stage.yieldOrCostApr >= 0 ? 'text-accent-green' : 'text-red-400' },
              { label: 'Daily earn', value: (stage.dailyYieldOrCostUsd >= 0 ? '+' : '') + usd(stage.dailyYieldOrCostUsd),
                color: stage.dailyYieldOrCostUsd >= 0 ? 'text-accent-green' : 'text-red-400' },
            ]}
            signals={stage.reasonCodes.length > 0 ? stage.reasonCodes : undefined}
          />
        </div>
      </div>
    </div>
  )
}

function WalletRow({
  walletTotalUsd, solNative, walletTopUp, nextIsActive, onManage,
}: {
  walletTotalUsd: number
  solNative: number
  walletTopUp: WalletTopUpSuggestion | null
  nextIsActive: boolean
  onManage: () => void
}) {
  const urgency: Urgency | null = walletTopUp ? 'MEDIUM' : null
  const tdBase = `whitespace-nowrap transition-colors ${urgencyRowBg(urgency, true)}`

  return (
    <>
      <tr onClick={onManage} className="group h-10 cursor-pointer">
        <td className={`${tdBase} pl-2.5 pr-1 rounded-l-lg`}>
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold text-ink uppercase tracking-wide leading-tight">WALLET</span>
            <span className="text-[8px] text-gray-500 leading-tight">spot balances</span>
          </div>
        </td>
        <td className={`${tdBase} px-1 text-left`}>
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium text-ink">{fmt(solNative, 4)} SOL</span>
            <span className="text-[9px] text-gray-500">{usd(walletTotalUsd)}</span>
            {walletTopUp && (
              <span className="text-[9px] text-amber-400">
                need +{fmt(walletTopUp.shortfallSol, 3)} SOL
              </span>
            )}
          </div>
        </td>
        <td className={`${tdBase} px-1 text-left text-[11px] text-ink-muted`}>—</td>
        <td className={`${tdBase} px-1 text-left text-[11px] text-ink-muted hidden sm:table-cell`}>—</td>
        <td className={`${tdBase} px-1 text-left hidden sm:table-cell`}>
          <span className="text-gray-400">—</span>
        </td>
        <td className={`${tdBase} px-1`}>
          <div className="flex items-center justify-start pl-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-green shrink-0" />
          </div>
        </td>
        <td className={`${tdBase} pl-1 pr-2.5 rounded-r-lg`}>
          <div className="flex items-center justify-end gap-1">
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase
              ${walletTopUp ? 'bg-amber-400/20 text-amber-300' : 'bg-accent-green/20 text-accent-green'}`}>
              {walletTopUp ? 'TOP UP' : 'READY'}
            </span>
            <button
              onClick={e => { e.stopPropagation(); onManage() }}
              className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-accent-purple/20 hover:bg-accent-purple/40 text-accent-purple transition-colors"
            >
              Manage
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={7} className="h-3.5 p-0">
          <div className="flex items-center justify-start pl-[22px] h-3.5">
            <svg viewBox="0 0 8 14" className={`w-2 h-3.5 ${nextIsActive ? 'text-ink/35' : 'text-ink/12'}`}>
              <line x1="4" y1="0" x2="4" y2="10" stroke="currentColor" strokeWidth="1.2"
                strokeDasharray={nextIsActive ? undefined : '2.5 1.5'} />
              <polyline points="1,7 4,11 7,7" fill="none" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </div>
        </td>
      </tr>
    </>
  )
}

function StageRow({
  stage, nextIsActive, isLast, dimmed, actions, expanded, onToggle, onGapClick, onManage, basePrice, priceMap, wsLp,
}: {
  stage: StageAllocation
  nextIsActive: boolean
  isLast: boolean
  dimmed?: boolean
  actions: RebalanceAction[]
  expanded: boolean
  onToggle: () => void
  onGapClick?: () => void
  onManage?: () => void
  basePrice: number
  priceMap: Record<string, number>
  wsLp?: import('../../types/lp').LpState[] | null
}) {
  const isYield = stage.yieldOrCostApr >= 0
  const hasActions = actions.length > 0
  const hasLpPositions = stage.stage === 'LP' && (wsLp ?? []).length > 0
  const isExpandable = hasActions || stage.status === 'RECOMMENDED' || hasLpPositions
  const hasGap = gapExceedsThreshold(stage)
  const topUrgency = actions.reduce<RebalanceAction['urgency'] | null>((best, a) => {
    if (!best) return a.urgency
    const rank = { HIGH: 3, MEDIUM: 2, LOW: 1 }
    return rank[a.urgency] > rank[best] ? a.urgency : best
  }, null)

  const isClickable = hasGap || isExpandable
  // Tint the row by urgency so action-required stages stand out at a glance.
  // HIGH = red, MEDIUM = amber, LOW = soft yellow, none = default surface.
  const rowUrgency = effectiveUrgency(stage, topUrgency)
  const tdBase = `whitespace-nowrap transition-colors ${urgencyRowBg(rowUrgency, isClickable)}`

  return (
    <>
      {/* Main row */}
      <tr
        onClick={hasGap ? onGapClick : isExpandable ? onToggle : undefined}
        className={`group h-10 ${dimmed ? 'opacity-40' : ''} ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {/* Col 1: Action */}
        <td className={`${tdBase} pl-2.5 pr-1 rounded-l-lg`}>
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold text-ink uppercase tracking-wide leading-tight">
              {stage.stage}
            </span>
            {STAGE_FLOW[stage.stage] && (
              <span className="text-[8px] text-gray-500 leading-tight">{STAGE_FLOW[stage.stage]}</span>
            )}
          </div>
        </td>

        {/* Col 2: Amount */}
        <td className={`${tdBase} px-1 text-left`}>
          <div className="flex flex-col items-start">
            {stage.stage === 'LP' && stage.lpSolRatio != null && stage.lpStableRatio != null && basePrice > 0 ? (
              <>
                <span className="text-[10px] font-medium text-ink leading-tight">
                  {(stage.amountUsd * stage.lpSolRatio / basePrice).toFixed(3)} SOL
                </span>
                <span className="text-[10px] font-medium text-ink leading-tight">
                  + {(stage.amountUsd * stage.lpStableRatio).toFixed(2)} USDC
                </span>
                <span className="text-[9px] text-gray-500">{usd(stage.amountUsd)}</span>
              </>
            ) : (() => {
              const asset = STAGE_ASSET[stage.stage]
              if (asset && stage.amountUsd > 0) {
                const price = priceMap[asset.mint] ?? basePrice
                if (price > 0) {
                  const native = stage.amountUsd / price
                  const decimals = asset.mint === USDC_MINT ? 2 : 4
                  return (
                    <>
                      <span className="text-xs font-medium text-ink">
                        {fmt(native, decimals)} {asset.symbol}
                      </span>
                      <span className="text-[9px] text-gray-500">{usd(stage.amountUsd)}</span>
                    </>
                  )
                }
              }
              return <span className="text-xs font-medium text-ink">{usd(stage.amountUsd)}</span>
            })()}
            {stage.actualAmountUsd != null && (() => {
              const asset = STAGE_ASSET[stage.stage]
              if (asset) {
                const price = priceMap[asset.mint] ?? basePrice
                if (price > 0) {
                  const native = stage.actualAmountUsd / price
                  const decimals = asset.mint === USDC_MINT ? 2 : 2
                  return (
                    <span className="text-[9px] text-gray-500">
                      A: {fmt(native, decimals)} {asset.symbol}
                    </span>
                  )
                }
              }
              return <span className="text-[9px] text-gray-500">A: {usd(stage.actualAmountUsd)}</span>
            })()}
            {stage.gapUsd != null && Math.abs(stage.gapUsd) > 1 && (() => {
              const asset = STAGE_ASSET[stage.stage]
              if (asset) {
                const price = priceMap[asset.mint] ?? basePrice
                if (price > 0) {
                  const nativeGap = stage.gapUsd / price
                  const decimals = asset.mint === USDC_MINT ? 2 : 2
                  return (
                    <span className="text-[9px] text-amber-400">
                      {nativeGap > 0 ? '+' : ''}{fmt(Math.abs(nativeGap), decimals)} {asset.symbol}
                    </span>
                  )
                }
              }
              return (
                <span className="text-[9px] text-amber-400">
                  {stage.gapUsd > 0 ? '+' : ''}{usd(stage.gapUsd)}
                </span>
              )
            })()}
          </div>
        </td>

        {/* Col 3: APY */}
        <td className={`${tdBase} px-1 text-left text-[11px] font-medium whitespace-nowrap
          ${isYield ? 'text-accent-green' : 'text-red-400'}`}>
          {isYield ? '+' : ''}{pct(stage.yieldOrCostApr)}
        </td>

        {/* Col 4: Target Earn */}
        <td className={`${tdBase} px-1 text-left text-[11px] whitespace-nowrap hidden sm:table-cell
          ${isYield ? 'text-accent-green/70' : 'text-red-400/70'}`}>
          {isYield ? '+' : ''}{usd(stage.dailyYieldOrCostUsd)}/d
        </td>

        {/* Col 5: Current Earn */}
        <td className={`${tdBase} px-1 text-left whitespace-nowrap hidden sm:table-cell`}>
          {stage.actualAmountUsd != null && stage.actualAmountUsd > 0 ? (() => {
            const curDaily = stage.actualAmountUsd * stage.yieldOrCostApr / 365
            const curIsYield = curDaily >= 0
            return (
              <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${
                curIsYield ? 'bg-teal-400/15 text-teal-400' : 'bg-[#f97066]/15 text-[#f97066]'
              }`}>
                {curIsYield ? '+' : ''}{usd(Math.abs(curDaily))}/d
              </span>
            )
          })() : <span className="text-gray-400">—</span>}
        </td>

        {/* Col 5: State (risk dot) */}
        <td className={`${tdBase} px-1`}>
          <div className="flex items-center justify-start pl-1">
            {riskDot(stage.riskLevel)}
          </div>
        </td>

        {/* Col 6: Status + expand indicator */}
        <td className={`${tdBase} pl-1 pr-2.5 rounded-r-lg`}>
          <div className="flex items-center justify-end gap-1">
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase
              ${statusColor(stage.status)}`}>
              {stage.status}
            </span>
            {onManage && !dimmed && (
              <button
                onClick={e => { e.stopPropagation(); onManage() }}
                className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-accent-purple/20 hover:bg-accent-purple/40 text-accent-purple transition-colors"
              >
                Manage
              </button>
            )}
            {hasGap && (
              <div className="flex items-center gap-1 shrink-0">
                <span className={`text-[9px] font-semibold ${(stage.gapUsd ?? 0) > 0 ? 'text-amber-400' : 'text-orange-400'}`}>
                  {(stage.gapUsd ?? 0) > 0 ? '+' : '−'}{usd(Math.abs(stage.gapUsd ?? 0))}
                </span>
                <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-amber-400/70"
                  fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="2,4 6,8 10,4" />
                </svg>
              </div>
            )}
            {!hasGap && isExpandable && (
              <div className="flex items-center gap-0.5 shrink-0">
                {hasActions && (
                  <span className={`text-[9px] font-bold
                    ${topUrgency === 'HIGH' ? 'text-red-400' : topUrgency === 'MEDIUM' ? 'text-amber-400' : 'text-ink-muted'}`}>
                    {actions.length}
                  </span>
                )}
                <svg viewBox="0 0 12 12"
                  className={`w-2.5 h-2.5 transition-transform ${expanded ? 'rotate-180' : ''}
                    ${hasActions
                      ? topUrgency === 'HIGH' ? 'text-red-400' : topUrgency === 'MEDIUM' ? 'text-amber-400' : 'text-ink-muted'
                      : 'text-accent-purple/60'}`}
                  fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="2,4 6,8 10,4" />
                </svg>
              </div>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded panel */}
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-surface-hover border-t border-fringe-strong px-2.5 py-2 rounded-b-lg">
            <div className="flex flex-col gap-2">
              {(stage.status === 'RECOMMENDED' || hasLpPositions) && <RecommendedDetail stage={stage} wsLp={wsLp} />}
              {actions.map((a, i) => (
                <ActionDetail key={`${a.stage}-${a.actionType}-${i}`} action={a} />
              ))}
            </div>
          </td>
        </tr>
      )}

      {/* Connector arrow */}
      {!isLast && (
        <tr>
          <td colSpan={7} className="h-3.5 p-0">
            <div className="flex items-center justify-start pl-[22px] h-3.5">
              <svg viewBox="0 0 8 14" className={`w-2 h-3.5 ${nextIsActive ? 'text-ink/35' : 'text-ink/12'}`}>
                <line x1="4" y1="0" x2="4" y2="10" stroke="currentColor" strokeWidth="1.2"
                  strokeDasharray={nextIsActive ? undefined : '2.5 1.5'} />
                <polyline points="1,7 4,11 7,7" fill="none" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}


const NAV_PERIODS: { id: NavPeriod; label: string }[] = [
  { id: 'DAY',   label: 'Day'   },
  { id: 'WEEK',  label: 'Week'  },
  { id: 'MONTH', label: 'Month' },
  { id: 'YEAR',  label: 'Year'  },
]

// Median filter — rejects RPC outlier spikes/gaps without blending them in
function medianFilter(pts: NavPoint[], halfWindow: number): NavPoint[] {
  return pts.map((p, i) => {
    const lo   = Math.max(0, i - halfWindow)
    const hi   = Math.min(pts.length - 1, i + halfWindow)
    const vals = pts.slice(lo, hi + 1).map(x => parseFloat(x.navUsd))
    vals.sort((a, b) => a - b)
    const med  = vals[Math.floor(vals.length / 2)]
    return { ts: p.ts, navUsd: String(med) }
  })
}

function NavHistoryChart({ portfolioId }: { portfolioId: string }) {
  const [period, setPeriod]   = useState<NavPeriod>('WEEK')
  const [points, setPoints]   = useState<NavPoint[]>([])
  const [loading, setLoading] = useState(false)
  const [empty, setEmpty]     = useState(false)

  useEffect(() => {
    setLoading(true)
    fetchNavHistory(portfolioId, period)
      .then(data => { setPoints(data); setEmpty(data.length < 2); setLoading(false) })
      .catch(() => { setEmpty(true); setLoading(false) })
  }, [portfolioId, period])

  // Filter out partial startup snapshots (NAV < 50% of median = incomplete position load)
  const filtered = (() => {
    if (points.length < 3) return points
    const vals = points.map(p => parseFloat(p.navUsd)).sort((a, b) => a - b)
    const median = vals[Math.floor(vals.length / 2)]
    const threshold = median * 0.5
    return points.filter(p => parseFloat(p.navUsd) >= threshold)
  })()

  // Median filter: halfWindow=2 (window of 5) rejects isolated RPC outliers
  const smoothed  = filtered.length >= 2 ? medianFilter(filtered, 2) : filtered

  // Period change: first value in the period → last value in the period
  const firstNav  = filtered.length > 0 ? parseFloat(filtered[0].navUsd) : null
  const latestNav = filtered.length > 0 ? parseFloat(filtered[filtered.length - 1].navUsd) : null
  const changeDelta = latestNav != null && firstNav != null ? latestNav - firstNav : null
  const changePct = firstNav != null && firstNav > 0 && changeDelta != null
    ? (changeDelta / firstNav) * 100 : null
  const isUp = changePct == null || changePct >= 0

  // SVG layout constants (viewBox-based — scales with container)
  const W  = 600
  const H  = 150
  const PL = 52   // left padding for y-axis labels
  const PR = 8
  const PT = 10
  const PB = 24   // bottom padding for x-axis labels

  const chartW = W - PL - PR
  const chartH = H - PT - PB

  // Build scales from smoothed data
  const navVals  = smoothed.map(p => parseFloat(p.navUsd))
  const tsVals   = smoothed.map(p => p.ts)
  const navMin   = Math.min(...navVals)
  const navMax   = Math.max(...navVals)
  const tsMin    = Math.min(...tsVals)
  const tsMax    = Math.max(...tsVals)
  const navRange = navMax - navMin || 1
  const tsRange  = tsMax  - tsMin  || 1

  const xScale = (ts: number)  => PL + ((ts  - tsMin)  / tsRange)  * chartW
  const yScale = (nav: number) => PT + (1 - (nav - navMin) / navRange) * chartH

  // d3-shape line + area with CatmullRom smoothing
  const lineGen = d3line<NavPoint>()
    .x(p => xScale(p.ts))
    .y(p => yScale(parseFloat(p.navUsd)))
    .curve(curveCatmullRom.alpha(0.5))

  const areaGen = d3area<NavPoint>()
    .x(p  => xScale(p.ts))
    .y0(PT + chartH)
    .y1(p  => yScale(parseFloat(p.navUsd)))
    .curve(curveCatmullRom.alpha(0.5))

  const linePath = smoothed.length >= 2 ? (lineGen(smoothed) ?? '') : ''
  const areaPath = smoothed.length >= 2 ? (areaGen(smoothed) ?? '') : ''

  // Y-axis ticks (3 levels)
  const yTicks = [navMin, navMin + navRange / 2, navMax]

  // X-axis ticks (5 evenly spaced timestamps)
  const xTickCount = 5
  const xTicks = Array.from({ length: xTickCount }, (_, i) => tsMin + (tsRange * i) / (xTickCount - 1))

  function fmtTs(ts: number) {
    const d = new Date(ts * 1000)
    if (period === 'DAY')   return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    if (period === 'YEAR')  return d.toLocaleDateString([], { month: 'short', year: '2-digit' })
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  const lineColor  = isUp ? '#6D5BF7' : '#ef4444'
  const gradId     = `nav-grad-${period}`

  return (
    <div className="border-t border-fringe pt-3 flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center justify-between px-0.5">
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Total Portfolio Value</span>
          {latestNav != null && (
            <span className="text-xs font-semibold text-ink">
              ${latestNav.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
          {changePct != null && (
            <span className={`text-[10px] font-medium ${isUp ? 'text-accent-green' : 'text-red-400'}`}>
              {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
            </span>
          )}
          {changeDelta != null && Math.abs(changeDelta) >= 0.01 && (
            <span className={`text-[10px] font-medium ${isUp ? 'text-teal-400/80' : 'text-[#f97066]'}`}>
              {changeDelta >= 0 ? '+' : ''}${Math.abs(changeDelta).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>
        <div className="flex gap-0.5">
          {NAV_PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`text-[9px] px-1.5 py-0.5 rounded font-medium transition-colors
                ${period === p.id
                  ? 'bg-accent-purple text-white'
                  : 'bg-surface-hover text-gray-500 hover:bg-surface-hover hover:text-ink-soft'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* SVG chart */}
      <div className="relative w-full" style={{ height: H }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-4 h-4 animate-spin text-accent-purple" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5"/>
              <path className="opacity-80" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        {!loading && empty && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400">
            No history yet for this period
          </div>
        )}
        {!loading && smoothed.length >= 2 && (
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={lineColor} stopOpacity="0.18" />
                <stop offset="100%" stopColor={lineColor} stopOpacity="0"    />
              </linearGradient>
            </defs>

            {/* Horizontal grid lines */}
            {yTicks.map((nav, i) => {
              const y = yScale(nav)
              return (
                <g key={i}>
                  <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#EEF1F8" strokeWidth="1" />
                  <text x={PL - 4} y={y + 3.5} textAnchor="end" fontSize="9" fill="#4b5563">
                    ${nav >= 1000 ? (nav / 1000).toFixed(1) + 'k' : nav.toFixed(0)}
                  </text>
                </g>
              )
            })}

            {/* X-axis tick labels */}
            {xTicks.map((ts, i) => (
              <text
                key={i}
                x={xScale(ts)}
                y={H - 4}
                textAnchor={i === 0 ? 'start' : i === xTickCount - 1 ? 'end' : 'middle'}
                fontSize="9"
                fill="#4b5563"
              >
                {fmtTs(ts)}
              </text>
            ))}

            {/* Area fill */}
            <path d={areaPath} fill={`url(#${gradId})`} />

            {/* Smooth line */}
            <path d={linePath} fill="none" stroke={lineColor} strokeWidth="1.8" strokeLinejoin="round" />

            {/* Latest value dot */}
            {(() => {
              const last = smoothed[smoothed.length - 1]
              return (
                <circle
                  cx={xScale(last.ts)}
                  cy={yScale(parseFloat(last.navUsd))}
                  r="3"
                  fill={lineColor}
                  stroke="#E8EEFF"
                  strokeWidth="1.5"
                />
              )
            })()}
          </svg>
        )}
      </div>
    </div>
  )
}

function MarketSignalBar({ mc, onInfoClick }: { mc: MarketContext; onInfoClick?: () => void }) {
  const directionStyle =
    mc.trendRegime === 'UPTREND'   ? 'bg-accent-green/20 text-accent-green' :
    mc.trendRegime === 'DOWNTREND' ? 'bg-red-500/20 text-red-400' :
                                     'bg-surface-hover text-ink-muted'
  const arrow = mc.trendRegime === 'UPTREND' ? '↑' : mc.trendRegime === 'DOWNTREND' ? '↓' : '→'
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-500 px-0.5">
      <span className={`px-1.5 py-0.5 rounded-full font-medium ${directionStyle}`}>
        {arrow} 4H trend {mc.trendRegime}
      </span>
      <span>RSI <span className="text-ink">{fmt(mc.rsi, 1)}</span></span>
      <span>ATR <span className="text-ink">{fmt(mc.atr, 2)}</span></span>
      <span>BRK <span className="text-ink">{pct(mc.breakoutProbability)}</span></span>
      <span>REJ <span className="text-ink">{pct(mc.rejectionProbability)}</span></span>
      <span className="text-gray-400">trendScore = <span className="text-ink">{fmt(mc.trendScore, 2)}</span> → <span className={
        mc.trendRegime === 'UPTREND' ? 'text-accent-green' : mc.trendRegime === 'DOWNTREND' ? 'text-red-400' : 'text-ink-muted'
      }>{mc.trendRegime}</span></span>
      {onInfoClick && (
        <button
          onClick={onInfoClick}
          className="ml-auto shrink-0 text-gray-500 hover:text-accent-purple transition-colors p-1 rounded hover:bg-accent-purple/10"
          title="Strategy guide for current regime"
        >
          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="8" cy="8" r="7"/>
            <line x1="8" y1="7" x2="8" y2="11.5"/>
            <circle cx="8" cy="5" r="0.5" fill="currentColor"/>
          </svg>
        </button>
      )}
    </div>
  )
}

export default function StrategyAllocationPanel({
  ready = false,
  wsWallet,
  wsLending,
  wsLp,
  wsPerps,
  onStakeStage,
  onLendBorrowStages,
  onLpStage,
  onHedgeStage,
}: {
  ready?: boolean
  wsWallet?: WalletBalance[] | null
  wsLending?: LendingPosition[] | null
  wsLp?: import('../../types/lp').LpState[] | null
  wsPerps?: import('../../types/perp').PerpPosition[] | null
  onStakeStage?: (stake: StageAllocation | null) => void
  onLendBorrowStages?: (lend: StageAllocation | null, borrow: StageAllocation | null) => void
  onLpStage?: (lp: StageAllocation | null) => void
  onHedgeStage?: (hedge: StageAllocation | null) => void
} = {}) {
  const { portfolioId } = useAuth()
  const [mode, setMode] = useState<StrategyMode>('BALANCED_CARRY')
  const [data, setData] = useState<StrategyAllocationResponse | null>(null)
  const [rebalance, setRebalance] = useState<RebalanceCheckResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Cold-start: backend returned 404 because the portfolio's first valuation
  // snapshot hasn't been written yet. We poll silently with exponential
  // backoff and don't render the red error text until retries are exhausted.
  const [coldStart, setColdStart] = useState(false)
  const retryStateRef = useRef<{ count: number; timer: number | null }>({ count: 0, timer: null })
  const COLD_START_MAX_RETRIES = 6
  const [expandedStage, setExpandedStage] = useState<string | null>(null)
  const [gapStage, setGapStage] = useState<StageAllocation | null>(null)
  const [showSwapManage, setShowSwapManage] = useState(false)
  const [lendBorrowSection, setLendBorrowSection] = useState<'collateral' | 'debt' | null>(null)
  const [showLpManage, setShowLpManage] = useState(false)
  const [showHedgeManage, setShowHedgeManage] = useState(false)
  const [priceMap, setPriceMap] = useState<Record<string, number>>({})
  const [showGuide, setShowGuide] = useState(false)

  // Combined WS dep — shimmer fires when ANY of the four feeds pushes a new ref.
  // useMemo keys by identity so the hook can do === comparison.
  const wsCombined = useMemo(
    () => ({ wsWallet, wsLending, wsLp, wsPerps }),
    [wsWallet, wsLending, wsLp, wsPerps],
  )
  const shimmerRef = useShimmerOnUpdate(wsCombined)

  function buildPriceMap(balances: WalletBalance[]) {
    const map: Record<string, number> = {}
    for (const b of balances) {
      const price = parseFloat(b.priceUsd)
      if (b.mintAddress && price > 0) map[b.mintAddress] = price
    }
    return map
  }

  // Initial fetch on mount (before first WS push)
  useEffect(() => {
    if (!portfolioId) return
    fetchWalletBalances(portfolioId).then(b => setPriceMap(buildPriceMap(b))).catch(() => {})
  }, [portfolioId])

  // Live updates from WebSocket push
  useEffect(() => {
    if (!wsWallet) return
    setPriceMap(buildPriceMap(wsWallet))
  }, [wsWallet])

  // Override priceMap with accurate prices from backend market context
  const effectivePriceMap: Record<string, number> = { ...priceMap }
  if (data != null && (data.marketContext.basePrice ?? 0) > 0) effectivePriceMap[SOL_MINT] = data.marketContext.basePrice
  if (data != null && (data.marketContext.jupSolPrice ?? 0) > 0) effectivePriceMap[JUPSOL_MINT] = data.marketContext.jupSolPrice

  // Track which mode's data is currently displayed to know when to show the full spinner
  const loadedModeRef = useRef<StrategyMode | null>(null)

  // Whole-dollar fingerprint of all position buckets — changes only when the backend's
  // current-equity baseline materially changes. Used to trigger a silent re-fetch of the
  // allocation so suggestions track newly-deposited staked SOL (e.g. jupSOL) without
  // waiting for the user to switch modes or reload.
  const positionsFingerprint = useMemo(() => {
    const walletUsd  = (wsWallet  ?? []).reduce((s, b) => s + parseFloat(b.usdValue || '0'), 0)
    const lendSupply = (wsLending ?? []).filter(p => p.positionType === 'LENDING')
                                        .reduce((s, p) => s + parseFloat(p.usdValue || '0'), 0)
    const lendBorrow = (wsLending ?? []).filter(p => p.positionType === 'BORROW')
                                        .reduce((s, p) => s + parseFloat(p.usdValue || '0'), 0)
    const lpUsd      = (wsLp      ?? []).reduce((s, p) => s + parseFloat(p.lpValueUsd    || '0'), 0)
    const perpsUsd   = (wsPerps   ?? []).reduce((s, p) => s + parseFloat(p.collateralUsd || '0'), 0)
    return [walletUsd, lendSupply, lendBorrow, lpUsd, perpsUsd].map(v => v.toFixed(0)).join('|')
  }, [wsWallet, wsLending, wsLp, wsPerps])

  const refetchAllocation = useCallback((silent: boolean) => {
    if (!portfolioId || !ready) return
    if (!silent) setLoading(true)
    setError(null)
    Promise.all([
      fetchStrategyAllocation(portfolioId, mode),
      fetchRebalanceCheck(portfolioId, mode).catch(() => null),
    ]).then(([alloc, reb]) => {
      loadedModeRef.current = mode
      setData(alloc)
      setRebalance(reb)
      setLoading(false)
      setColdStart(false)
      // Clear any pending cold-start retry — we're warm now.
      if (retryStateRef.current.timer != null) {
        window.clearTimeout(retryStateRef.current.timer)
        retryStateRef.current = { count: 0, timer: null }
      }
      onStakeStage?.(alloc.stake)
      onLendBorrowStages?.(alloc.lend, alloc.borrow)
      onLpStage?.(alloc.lp)
      onHedgeStage?.(alloc.hedge)
    }).catch(e => {
      const msg = e?.message ?? 'Error'
      // Backend returns 404 while PortfolioValuationCache is still warming
      // (first snapshot not yet written). Don't surface this as a hard error
      // — silently retry with exponential backoff so the panel auto-recovers
      // a few seconds after login without forcing the user to switch modes.
      const isCold = typeof msg === 'string' && msg.startsWith('404')
      if (isCold && retryStateRef.current.count < COLD_START_MAX_RETRIES) {
        const attempt = retryStateRef.current.count
        const delay = Math.min(16_000, 1_000 * Math.pow(2, attempt))
        setColdStart(true)
        setLoading(false)
        if (retryStateRef.current.timer != null) window.clearTimeout(retryStateRef.current.timer)
        retryStateRef.current.timer = window.setTimeout(() => {
          retryStateRef.current.count += 1
          retryStateRef.current.timer = null
          refetchAllocation(true)
        }, delay)
        return
      }
      setError(msg)
      setColdStart(false)
      setLoading(false)
    })
  }, [portfolioId, mode, ready, onStakeStage, onLendBorrowStages, onLpStage, onHedgeStage])

  useEffect(() => {
    // Reset retry state on every change in identity or mode — a stale timer
    // from a previous mode/login should not fire against the new one.
    if (retryStateRef.current.timer != null) {
      window.clearTimeout(retryStateRef.current.timer)
    }
    retryStateRef.current = { count: 0, timer: null }
    // Full spinner only on initial load or after a mode switch; otherwise silent.
    refetchAllocation(loadedModeRef.current === mode)
    return () => {
      if (retryStateRef.current.timer != null) {
        window.clearTimeout(retryStateRef.current.timer)
        retryStateRef.current.timer = null
      }
    }
  }, [portfolioId, mode, ready, refetchAllocation])

  // Silent re-fetch when the user's position totals change (e.g. after a new jupSOL deposit
  // publishes a ProtocolPositionEvent, the backend recomputes equityUsd, the WS delta lands
  // here, and this effect picks up the fresh allocation baseline). 700 ms debounce lets the
  // backend's 1.5 s recompute and Redis write settle before we fetch.
  useEffect(() => {
    // While we have data we wait for the initial fetch of THIS mode to land.
    // While we DON'T have data (first login → 404 cold-start), we want every
    // first WS push to also retry the allocation — that's how the panel
    // auto-recovers without the user having to touch the mode pills.
    if (data != null && loadedModeRef.current !== mode) return
    const t = setTimeout(() => refetchAllocation(true), 700)
    return () => clearTimeout(t)
  }, [positionsFingerprint, mode, data, refetchAllocation])

  // Group rebalance actions by allocation stage name
  const actionsByStage: Record<string, RebalanceAction[]> = {}
  if (rebalance) {
    for (const action of rebalance.actions) {
      for (const [stageName, rebalStages] of Object.entries(STAGE_MAP)) {
        if (rebalStages.includes(action.stage)) {
          if (!actionsByStage[stageName]) actionsByStage[stageName] = []
          actionsByStage[stageName].push(action)
        }
      }
    }
  }

  // Wallet / perps totals — wallet is displayed as "reserve have"; perps kept for any consumer.
  const walletTotalUsd = (wsWallet ?? []).reduce((sum, b) => sum + parseFloat(b.usdValue || '0'), 0)
  const perpsTotalUsd = (wsPerps ?? []).reduce((sum, p) => sum + parseFloat(p.collateralUsd || '0'), 0)
  // New model: Deployed tile = staked-LST baseline (what the backend anchors strategy on).
  // Reserve tile = backend-computed required reserve; "have" shown as pill.
  const actualDeployedUsd = data ? data.totalDeployedUsd : 0
  const reserveHaveUsd = data?.walletTopUp?.currentWalletUsd ?? walletTotalUsd
  void perpsTotalUsd // retained for future — perps collateral no longer counts toward reserve

  const stages: { stage: StageAllocation; nextIsActive: boolean }[] = data ? [
    { stage: data.lend,   nextIsActive: data.borrow.status !== 'SKIPPED' && data.borrow.status !== 'BLOCKED' },
    { stage: data.borrow, nextIsActive: data.lp.status     !== 'SKIPPED' && data.lp.status     !== 'BLOCKED' },
    { stage: data.lp,     nextIsActive: data.hedge.status  !== 'SKIPPED' && data.hedge.status  !== 'BLOCKED' },
    { stage: data.hedge,  nextIsActive: false },
  ] : []

  const solNative = useMemo(() => {
    const entry = (wsWallet ?? []).find(b => b.mintAddress === SOL_MINT)
    return entry ? parseFloat(entry.amount) : 0
  }, [wsWallet])

  return (
    <>
    {gapStage && (
      <GapRecommendationPanel
        stage={gapStage}
        priceMap={effectivePriceMap}
        basePrice={data?.marketContext.basePrice ?? 0}
        mode={mode}
        onClose={() => setGapStage(null)}
      />
    )}
    {lendBorrowSection !== null && (
      <JupiterBorrowPanel
        positions={wsLending ?? []}
        onClose={() => setLendBorrowSection(null)}
        lendStage={data?.lend}
        borrowStage={data?.borrow}
        initialSection={lendBorrowSection}
      />
    )}
    {showLpManage && (
      <LpManagePanel
        positions={wsLp ?? []}
        lpStage={data?.lp}
        onClose={() => setShowLpManage(false)}
      />
    )}
    {showHedgeManage && (
      <JupiterPerpsPanel
        positions={wsPerps ?? []}
        hedgeStage={data?.hedge}
        onClose={() => setShowHedgeManage(false)}
      />
    )}
    {showSwapManage && (
      <JupiterSwapPanel
        onClose={() => setShowSwapManage(false)}
        walletTopUp={data?.walletTopUp ?? null}
      />
    )}
    {showGuide && data && (
      <StrategyGuidePanel
        data={data}
        rebalance={rebalance}
        mode={mode}
        onClose={() => setShowGuide(false)}
      />
    )}
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-fringe flex-wrap">
        <div className="w-6 h-6 rounded-lg bg-accent-purple/10 flex items-center justify-center text-accent-purple shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <span className="text-xs font-semibold text-ink">Strategy Allocation</span>

        {/* Overall rebalance status badge */}
        {rebalance && (
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide
            ${overallStatusStyle(rebalance.overallStatus)}`}>
            {overallStatusLabel(rebalance.overallStatus)}
          </span>
        )}

        <div className="ml-auto flex gap-1">
          {MODES.map(m => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`text-[10px] px-2 py-1 rounded font-medium transition-colors
                ${mode === m.id
                  ? 'bg-accent-purple text-white'
                  : 'bg-surface-hover text-ink-muted hover:bg-surface-hover hover:text-ink'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="p-3 flex flex-col gap-3 flex-1">
        {/* Full spinner only on initial load / mode switch — not on background refresh */}
        {loading && !data && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-gray-500">
            <svg className="w-5 h-5 animate-spin text-accent-purple" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5"/>
              <path className="opacity-80" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <span className="text-xs">Computing…</span>
          </div>
        )}
        {coldStart && !data && !loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-ink-muted">
            <svg className="w-5 h-5 animate-spin text-accent-purple" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5"/>
              <path className="opacity-80" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <span className="text-xs">Preparing your portfolio — this normally takes a few seconds after login…</span>
          </div>
        )}
        {error && !loading && !coldStart && (
          <div className="text-xs text-red-400 text-center py-3">{error}</div>
        )}

        {data && (
          <>
            {/* Market signal bar */}
            <MarketSignalBar mc={data.marketContext} onInfoClick={() => setShowGuide(true)} />

            {/* Stage table */}
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 pl-2.5 pr-1">Action</th>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 px-1">Amount</th>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 px-1">APY</th>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 px-1 hidden sm:table-cell">Earn</th>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 px-1 hidden sm:table-cell">Cur.</th>
                  <th className="whitespace-nowrap text-[9px] text-gray-400 uppercase tracking-wide font-normal text-left pb-0.5 px-1 pl-2">State</th>
                  <th className="w-full text-[9px] text-gray-400 uppercase tracking-wide font-normal text-right pb-0.5 pl-1 pr-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                <WalletRow
                  walletTotalUsd={walletTotalUsd}
                  solNative={solNative}
                  walletTopUp={data.walletTopUp}
                  nextIsActive={data.lend.status !== 'SKIPPED' && data.lend.status !== 'BLOCKED'}
                  onManage={() => setShowSwapManage(true)}
                />
                {stages.map(({ stage, nextIsActive }, i) => {
                  const stageActions = actionsByStage[stage.stage] ?? []
                  return (
                    <StageRow
                      key={stage.stage}
                      stage={stage}
                      nextIsActive={nextIsActive}
                      isLast={i === stages.length - 1}
                      dimmed={stage.status === 'SKIPPED' || stage.status === 'BLOCKED'}
                      actions={stageActions}
                      expanded={expandedStage === stage.stage}
                      onToggle={() => setExpandedStage(
                        expandedStage === stage.stage ? null : stage.stage
                      )}
                      onGapClick={() =>
                        stage.stage === 'LEND' ? setLendBorrowSection('collateral')
                        : stage.stage === 'BORROW' ? setLendBorrowSection('debt')
                        : stage.stage === 'LP' ? setShowLpManage(true)
                        : stage.stage === 'HEDGE' ? setShowHedgeManage(true)
                        : setGapStage(stage)
                      }
                      onManage={
                        stage.stage === 'LEND' ? () => setLendBorrowSection('collateral') :
                        undefined
                      }
                      basePrice={data.marketContext.basePrice}
                      priceMap={effectivePriceMap}
                      wsLp={wsLp}
                    />
                  )
                })}
              </tbody>
            </table>

            {/* Summary blocks — target value + current pill on same row */}
            <div className="border-t border-fringe pt-2">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">Deployed</span>
                  <span className="text-[10px] font-semibold text-ink mt-1 leading-none">{usd(actualDeployedUsd)}</span>
                </div>
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">Reserve</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-[10px] font-semibold text-ink leading-none">{usd(data.reserveUsd)}</span>
                    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full leading-none ${
                      data.walletTopUp ? 'bg-amber-400/15 text-amber-400' : 'bg-teal-400/15 text-teal-400'
                    }`}>
                      {usd(reserveHaveUsd)}
                    </span>
                  </div>
                </div>
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">Carry/day</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`text-[10px] font-semibold leading-none ${data.netCarryPerDayUsd >= 0 ? 'text-accent-green' : 'text-red-400'}`}>
                      {(data.netCarryPerDayUsd >= 0 ? '+' : '') + usd(data.netCarryPerDayUsd)}
                    </span>
                    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full leading-none ${
                      data.actualNetCarryPerDayUsd >= 0 ? 'bg-teal-400/15 text-teal-400' : 'bg-[#f97066]/15 text-[#f97066]'
                    }`}>
                      {(data.actualNetCarryPerDayUsd >= 0 ? '+' : '') + usd(data.actualNetCarryPerDayUsd)}
                    </span>
                  </div>
                </div>
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">30d</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`text-[10px] font-semibold leading-none ${data.projectedGrowth30dUsd >= 0 ? 'text-accent-green' : 'text-red-400'}`}>
                      {(data.projectedGrowth30dUsd >= 0 ? '+' : '') + usd(data.projectedGrowth30dUsd)}
                    </span>
                    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full leading-none ${
                      data.actualProjectedGrowth30dUsd >= 0 ? 'bg-teal-400/15 text-teal-400' : 'bg-[#f97066]/15 text-[#f97066]'
                    }`}>
                      {(data.actualProjectedGrowth30dUsd >= 0 ? '+' : '') + usd(data.actualProjectedGrowth30dUsd)}
                    </span>
                  </div>
                </div>
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">Risk</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`text-[10px] font-semibold leading-none ${riskScoreColor(data.compositeRiskScore)}`}>
                      {fmt(data.compositeRiskScore)}
                    </span>
                    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full leading-none ${
                      data.actualCompositeRiskScore < 0.65 ? 'bg-teal-400/15 text-teal-400' : 'bg-[#f97066]/15 text-[#f97066]'
                    }`}>
                      {fmt(data.actualCompositeRiskScore)}
                    </span>
                  </div>
                </div>
                <div className="bg-surface-hover rounded-lg px-1.5 py-1.5 flex flex-col justify-between">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wide leading-none">HF</span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-[10px] font-semibold text-ink leading-none">
                      {data.projectedHealthFactor >= 999998 ? '∞' : fmt(data.projectedHealthFactor)}
                    </span>
                    <span className="text-[8px] font-medium px-1.5 py-0.5 rounded-full bg-teal-400/15 text-teal-400 leading-none">
                      {data.actualProjectedHealthFactor >= 999998 ? '∞' : fmt(data.actualProjectedHealthFactor)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* NAV history chart */}
            {portfolioId && <NavHistoryChart portfolioId={portfolioId} />}
          </>
        )}
      </div>
    </div>
    </>
  )
}
